// /////////////////////////////////////////////////////////////
// [ファイル] assistantDrafts.ts
// [概要] Workspace アシスタント入力欄の「書きかけメッセージ (ドラフト)」を
//        セッション単位で Firestore に永続化するモジュール。
//        1 ユーザー 1 ドキュメント (HozenallCloud_Assistant_Drafts/{email}) に
//        `drafts` マップで保持し、キーは "<agentType>__<sessionId|new>"。
//        未保存の新規チャットはグループ別に枠を分け、グループ付きは
//        "<agentType>__new__<groupId>" とする (共通は "new" のまま)。
//        書込はデバウンスでまとめ、キャッシュはモジュールスコープに置くことで
//        ページ遷移 (アンマウント) 後もフラッシュが完走する。
// /////////////////////////////////////////////////////////////
import {
  doc,
  getDoc,
  setDoc,
  deleteField,
  type FieldValue,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

const COLLECTION = 'HozenallCloud_Assistant_Drafts';
// タイプ中の毎キー書込を避けるための Firestore 書込デバウンス
const FLUSH_DELAY_MS = 1500;
// これより古いドラフトは読込時に破棄する (書きかけの鮮度としては十分)
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface DraftEntry {
  text: string;
  updated_at: number; // エポックms (サーバー時刻不要のためクライアント時刻)
}

// --- モジュールスコープの状態 (コンポーネントのアンマウントに影響されない) ------
let currentEmail: string | null = null;
let cache: Record<string, DraftEntry> = {};
let loadPromise: Promise<void> | null = null;
let loaded = false;
// 未フラッシュのキー集合 (値は cache 側が正。cache に無ければ削除予約)
const dirtyKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function docRef(email: string) {
  return doc(db, COLLECTION, email);
}

/**
 * ドラフトキーを組み立てる ("gws__abc123" / "accounting__new" 形式)。
 * セッション未選択 (新規チャット) のときは newChatGroupId でグループごとに枠を分ける
 * ("gws__new__<groupId>")。共通 ('') は従来どおり "gws__new"。
 */
export function assistantDraftKey(
  agentType: string,
  sessionId: string | null,
  newChatGroupId = ''
): string {
  if (sessionId) return `${agentType}__${sessionId}`;
  return newChatGroupId ? `${agentType}__new__${newChatGroupId}` : `${agentType}__new`;
}

/** 読込済みか (2回目以降のマウントでは同期復元できる) */
export function isAssistantDraftsReady(): boolean {
  return loaded;
}

/**
 * ユーザーのドラフト doc を一度だけ読み込む。多重呼び出しは同じ Promise を返す。
 * 期限切れエントリはここで間引く。読込前にローカルで入力済みのキーは
 * サーバー値で上書きしない (ローカル優先)。
 */
export function initAssistantDrafts(email: string): Promise<void> {
  if (currentEmail !== email) {
    // ユーザー切替 (通常は起きない) は状態を作り直す
    currentEmail = email;
    cache = {};
    dirtyKeys.clear();
    loaded = false;
    loadPromise = null;
  }
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const snap = await getDoc(docRef(email));
        const drafts = (snap.data()?.drafts ?? {}) as Record<string, DraftEntry>;
        const now = Date.now();
        for (const [key, entry] of Object.entries(drafts)) {
          if (!entry?.text || now - (entry.updated_at ?? 0) > DRAFT_TTL_MS) {
            // 期限切れ・空エントリは削除予約 (次回フラッシュで doc からも消す)
            if (!(key in cache)) dirtyKeys.add(key);
            continue;
          }
          // 読込完了前にユーザーがタイプした分 (cache 済み) を優先する
          if (!(key in cache)) cache[key] = entry;
        }
        if (dirtyKeys.size > 0) scheduleFlush();
      } catch (err) {
        // 読込失敗 (オフライン等) でも入力自体は妨げない。以後の保存は成功し得る
        console.error('ドラフトの読込に失敗しました', err);
      } finally {
        loaded = true;
      }
    })();
  }
  return loadPromise;
}

/** 現在のドラフトを返す (未読込・未保存なら空文字) */
export function getAssistantDraft(key: string): string {
  return cache[key]?.text ?? '';
}

/** ドラフトを更新する。空文字は削除扱い。Firestore へはデバウンスで反映する */
export function setAssistantDraft(key: string, text: string): void {
  if (text) {
    cache[key] = { text, updated_at: Date.now() };
  } else {
    delete cache[key];
  }
  dirtyKeys.add(key);
  scheduleFlush();
}

/** ドラフトを即時削除する (送信成功時・セッション削除時) */
export function clearAssistantDraft(key: string): void {
  setAssistantDraft(key, '');
}

function scheduleFlush(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

/** 未反映キーを 1 回の setDoc(merge) でまとめて書き込む */
async function flush(): Promise<void> {
  if (!currentEmail || dirtyKeys.size === 0) return;
  const keys = [...dirtyKeys];
  dirtyKeys.clear();
  const drafts: Record<string, DraftEntry | FieldValue> = {};
  for (const key of keys) {
    drafts[key] = cache[key] ?? deleteField();
  }
  try {
    await setDoc(
      docRef(currentEmail),
      { user_id: currentEmail, drafts },
      { merge: true }
    );
  } catch (err) {
    // 失敗分は dirty へ戻し、次の入力契機で再送する
    keys.forEach((k) => dirtyKeys.add(k));
    console.error('ドラフトの保存に失敗しました', err);
  }
}
